import { onUnmounted, watch } from "@vue/composition-api";
import { Location } from "vue-router";
import { isTipMessageDetails as typeGuard } from "@scrile/api-provider/dist/api/TipProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useDoNotDisturb from "@/hooks/useDoNotDisturb";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import router from "@/router";
import { EVENTS } from "@/const";
import { NotificationFactory } from "../../index";

export default function(sendNotification: NotificationFactory) {
  const { authUser } = useAuthUser();
  const { doNotDisturb } = useDoNotDisturb();

  const newMessageNotification = (message: Message) => {
    const location: Location = { name: "messages", params: { userId: message.senderId } };
    const isIntoSenderThread = () => {
      return location.name === router.currentRoute.name && router.currentRoute?.params.userId === message.senderId;
    };

    if (!authUser.value || authUser.value.id === message.senderId || doNotDisturb.value || isIntoSenderThread()) {
      return;
    }

    const showNotification = (notificationText: string, location: Location) =>
      sendNotification({
        text: $t(`{0} ${notificationText}`, [message.senderUser?.screenName]),
        position: "top",
        image: {
          user: message.senderUser,
        },
        onNotificationClick: () => router.push(location),
      });

    if (
      typeGuard(message.details) &&
      router.currentRoute?.params.userId !== message.details.recipientId &&
      "" + authUser.value.id !== "" + message.details.senderId
    ) {
      const userId = String(message.details.senderId);
      showNotification("has sent you a tip", { name: location.name, params: { userId } });
      return;
    }

    if (parseInt(message.senderId) >= 1 && message.senderId !== authUser.value.id) {
      showNotification("has sent you a message", location);
      return;
    }
  };
  let unSubscribe = () => {};
  const subscribe = async () => {
    unSubscribe();
    const unsubscribe = await providers.MessagesProvider.subscribeToAllThreads(message => {
      emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, message);
      newMessageNotification(message);
    });
    unSubscribe = () => unsubscribe.unsubscribe();
  };
  watch(
    authUser,
    (n, o) => {
      if (!n) {
        unSubscribe();
      } else if (!o) {
        subscribe();
      }
    },
    { immediate: true }
  );
  onUnmounted(() => unSubscribe());
}
